import { useCallback, useEffect, useRef } from "react";

import launchRapidOrbitSfx from "../../../../assets/Roulette/01_launch_and_6_rapid_spins.mp3";
import seventhSlowdownLapSfx from "../../../../assets/Roulette/02_seventh_spin_slowdown_lap.mp3";
import numberBandTransitionSfx from "../../../../assets/Roulette/03_transition_to_number_band.mp3";
import pocketClickClickSfx from "../../../../assets/Roulette/04_pockets_hit_click.mp3";
import finalRestWinningPocketSfx from "../../../../assets/Roulette/05_final_rest_winning_pocket.mp3";

const introductionVoiceContext = require.context(
  "../../../../assets/Roulette/voices/00Introductions",
  false,
  /^\.\/(chantelle|jack|mae|trina)\.mp3$/
);

const numberShoutVoiceContext = require.context(
  "../../../../assets/Roulette/voices",
  true,
  /^\.\/(chantelle|jack|mae|trina)\/numbershout\/(00|0|[1-9]|[12][0-9]|3[0-6])\.mp3$/
);

const noticeShoutVoiceContext = require.context(
  "../../../../assets/Roulette/voices",
  true,
  /^\.\/(chantelle|jack|mae|trina)\/noticeshout\/(allbetslocked|lockinyourbets|betbeforespinning.*)\.mp3$/
);

const CROUPIER_IDS = new Set(["chantelle", "jack", "mae", "trina"]);
const ROULETTE_NUMBER_SHOUT_DELAY_MS = 500;
const ROULETTE_RESULT_NOTICE_AFTER_SHOUT_MS = 500;

const SFX_VOLUME = {
  launchRapidOrbit: 0.62,
  seventhSlowdownLap: 0.58,
  numberBandTransition: 0.58,
  pocketClickClick: 0.72,
  finalRestWinningPocket: 0.78,
  croupierIntro: 0.86,
  croupierNumberShout: 0.9,
  croupierNoticeShout: 0.88,
};

const EVENT_TO_SFX_KEY = {
  BALL_LAUNCH: "launchRapidOrbit",
  RAPID_WALL_ORBIT_START: "launchRapidOrbit",
  LATE_DECEL_START: "seventhSlowdownLap",
  SPIRAL_TO_NUMBER_BAND: "numberBandTransition",
  CLICK_1: "pocketClickClick",
  CLICK_2: null,
  FINAL_CLUNK: "finalRestWinningPocket",
};

const SFX_SOURCES = {
  launchRapidOrbit: launchRapidOrbitSfx,
  seventhSlowdownLap: seventhSlowdownLapSfx,
  numberBandTransition: numberBandTransitionSfx,
  pocketClickClick: pocketClickClickSfx,
  finalRestWinningPocket: finalRestWinningPocketSfx,
};

const CROSSFADE_MS = 85;
const DUPLICATE_EVENT_GUARD_MS = 120;

function normaliseAssetModule(assetModule) {
  return assetModule?.default || assetModule;
}

function normaliseCroupierId(croupierId) {
  const normalised = String(croupierId || "").trim().toLowerCase();

  return CROUPIER_IDS.has(normalised) ? normalised : "";
}

function normaliseRouletteResult(result) {
  const normalised = String(result ?? "").trim();

  if (normalised === "00" || normalised === "0") {
    return normalised;
  }

  const numericResult = Number(normalised);

  if (
    Number.isSafeInteger(numericResult) &&
    numericResult >= 1 &&
    numericResult <= 36
  ) {
    return String(numericResult);
  }

  return "";
}

function getNoticeShoutKey(message) {
  const normalisedMessage = String(message || "")
    .trim()
    .toUpperCase();

  if (normalisedMessage === "ALL BETS LOCKED") {
    return "allbetslocked";
  }

  if (normalisedMessage === "LOCK IN YOUR BETS") {
    return "lockinyourbets";
  }

  if (normalisedMessage === "BET BEFORE SPINNING") {
    return "betbeforespinning";
  }

  return "";
}

function getCroupierIntroSrc(croupierId) {
  const normalisedCroupierId = normaliseCroupierId(croupierId);

  if (!normalisedCroupierId) {
    return "";
  }

  try {
    return normaliseAssetModule(
      introductionVoiceContext(`./${normalisedCroupierId}.mp3`)
    );
  } catch (error) {
    console.warn(`Missing croupier introduction SFX for ${normalisedCroupierId}`, error);
    return "";
  }
}

function getCroupierNumberShoutSrc(croupierId, result) {
  const normalisedCroupierId = normaliseCroupierId(croupierId);
  const normalisedResult = normaliseRouletteResult(result);

  if (!normalisedCroupierId || !normalisedResult) {
    return "";
  }

  try {
    return normaliseAssetModule(
      numberShoutVoiceContext(
        `./${normalisedCroupierId}/numbershout/${normalisedResult}.mp3`
      )
    );
  } catch (error) {
    console.warn(
      `Missing croupier number shout SFX for ${normalisedCroupierId} ${normalisedResult}`,
      error
    );
    return "";
  }
}

function getCroupierNoticeShoutSrc(croupierId, message) {
  const normalisedCroupierId = normaliseCroupierId(croupierId);
  const noticeShoutKey = getNoticeShoutKey(message);

  if (!normalisedCroupierId || !noticeShoutKey) {
    return "";
  }

  const exactKey = `./${normalisedCroupierId}/noticeshout/${noticeShoutKey}.mp3`;
  const matchingKey =
    noticeShoutVoiceContext.keys().find((contextKey) =>
      contextKey.startsWith(`./${normalisedCroupierId}/noticeshout/${noticeShoutKey}`)
    ) || exactKey;

  try {
    return normaliseAssetModule(noticeShoutVoiceContext(matchingKey));
  } catch (error) {
    console.warn(
      `Missing croupier notice shout SFX for ${normalisedCroupierId} ${noticeShoutKey}`,
      error
    );
    return "";
  }
}

function safePauseAndReset(audio) {
  if (!audio) {
    return;
  }

  audio.pause();
  audio.currentTime = 0;
}

function clearFadeTimers(fadeTimersRef, key) {
  const timer = fadeTimersRef.current[key];

  if (timer) {
    window.clearInterval(timer);
    delete fadeTimersRef.current[key];
  }
}

function fadeOutAudio(audio, fadeTimersRef, key, fadeMs = CROSSFADE_MS) {
  if (!audio) {
    return;
  }

  clearFadeTimers(fadeTimersRef, key);

  if (fadeMs <= 0) {
    safePauseAndReset(audio);
    return;
  }

  const startVolume = audio.volume;
  const startedAt = performance.now();

  fadeTimersRef.current[key] = window.setInterval(() => {
    const progress = Math.min(1, (performance.now() - startedAt) / fadeMs);

    audio.volume = startVolume * (1 - progress);

    if (progress >= 1) {
      clearFadeTimers(fadeTimersRef, key);
      safePauseAndReset(audio);
    }
  }, 16);
}

function createAudioElement(src, volume) {
  const audio = new Audio(src);

  audio.preload = "auto";
  audio.loop = false;
  audio.volume = volume;

  return audio;
}

function waitForRouletteAudioDelay(durationMs) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, durationMs);
  });
}

function useRouletteSfx({ muted = false } = {}) {
  const audioRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const voiceEndedResolverRef = useRef(null);
  const fadeTimersRef = useRef({});
  const lastEventAtRef = useRef({});
  const unlockedRef = useRef(false);
  const mutedRef = useRef(muted);

  const stopVoiceAudio = useCallback((fadeMs = 60) => {
    const currentVoiceAudio = voiceAudioRef.current;

    if (!currentVoiceAudio) {
      if (voiceEndedResolverRef.current) {
        voiceEndedResolverRef.current();
        voiceEndedResolverRef.current = null;
      }

      return;
    }

    const resolveVoiceEnded = voiceEndedResolverRef.current;
    voiceEndedResolverRef.current = null;

    currentVoiceAudio.onended = null;
    currentVoiceAudio.onerror = null;

    if (resolveVoiceEnded) {
      resolveVoiceEnded();
    }

    fadeOutAudio(currentVoiceAudio, fadeTimersRef, "voice", fadeMs);
    voiceAudioRef.current = null;
  }, []);

  useEffect(() => {
    mutedRef.current = muted;

    if (muted && audioRef.current) {
      Object.entries(audioRef.current).forEach(([key, audio]) => {
        fadeOutAudio(audio, fadeTimersRef, key, 60);
      });

      stopVoiceAudio(60);
    }
  }, [muted, stopVoiceAudio]);

  useEffect(() => {
    audioRef.current = Object.fromEntries(
      Object.entries(SFX_SOURCES).map(([key, src]) => [
        key,
        createAudioElement(src, SFX_VOLUME[key] ?? 0.65),
      ])
    );

    return () => {
      Object.entries(fadeTimersRef.current).forEach(([, timer]) => {
        window.clearInterval(timer);
      });
      fadeTimersRef.current = {};

      if (audioRef.current) {
        Object.values(audioRef.current).forEach((audio) => safePauseAndReset(audio));
      }

      stopVoiceAudio(0);
      audioRef.current = null;
    };
  }, [stopVoiceAudio]);

  const unlockAudio = useCallback(() => {
    if (unlockedRef.current || !audioRef.current) {
      return;
    }

    unlockedRef.current = true;

    Object.values(audioRef.current).forEach((audio) => {
      const previousMuted = audio.muted;
      const previousVolume = audio.volume;

      audio.muted = true;
      audio.volume = 0;

      const playPromise = audio.play();

      if (playPromise?.then) {
        playPromise
          .then(() => {
            audio.pause();
            audio.currentTime = 0;
            audio.muted = previousMuted;
            audio.volume = previousVolume;
          })
          .catch(() => {
            audio.muted = previousMuted;
            audio.volume = previousVolume;
          });
      } else {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = previousMuted;
        audio.volume = previousVolume;
      }
    });
  }, []);

  useEffect(() => {
    const unlock = () => unlockAudio();

    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("touchstart", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });

    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [unlockAudio]);

  const fadeOutAllExcept = useCallback((keepKey) => {
    if (!audioRef.current) {
      return;
    }

    Object.entries(audioRef.current).forEach(([key, audio]) => {
      if (key !== keepKey && !audio.paused) {
        fadeOutAudio(audio, fadeTimersRef, key);
      }
    });
  }, []);

  const playSfx = useCallback(
    (key) => {
      const audio = audioRef.current?.[key];

      if (!audio || mutedRef.current) {
        return;
      }

      clearFadeTimers(fadeTimersRef, key);
      fadeOutAllExcept(key);

      audio.pause();
      audio.currentTime = 0;
      audio.volume = SFX_VOLUME[key] ?? 0.65;

      const playPromise = audio.play();

      if (playPromise?.catch) {
        playPromise.catch(() => {});
      }
    },
    [fadeOutAllExcept]
  );

  const playVoice = useCallback(
    (src, volume = SFX_VOLUME.croupierNumberShout) =>
      new Promise((resolve) => {
        stopVoiceAudio(45);

        if (!src || mutedRef.current) {
          resolve();
          return;
        }

        const voiceAudio = createAudioElement(src, volume);

        voiceAudioRef.current = voiceAudio;
        voiceEndedResolverRef.current = resolve;

        const cleanup = () => {
          if (voiceAudioRef.current === voiceAudio) {
            voiceAudioRef.current = null;
          }

          if (voiceEndedResolverRef.current === resolve) {
            voiceEndedResolverRef.current = null;
          }

          voiceAudio.onended = null;
          voiceAudio.onerror = null;
          resolve();
        };

        voiceAudio.onended = cleanup;
        voiceAudio.onerror = cleanup;

        const playPromise = voiceAudio.play();

        if (playPromise?.catch) {
          playPromise.catch(cleanup);
        }
      }),
    [stopVoiceAudio]
  );

  const emitRouletteSfx = useCallback(
    (eventName) => {
      const now = performance.now();
      const lastEventAt = lastEventAtRef.current[eventName] || 0;

      if (now - lastEventAt < DUPLICATE_EVENT_GUARD_MS) {
        return;
      }

      lastEventAtRef.current[eventName] = now;

      const key = EVENT_TO_SFX_KEY[eventName];

      if (!key) {
        return;
      }

      playSfx(key);
    },
    [playSfx]
  );

  const playCroupierIntro = useCallback(
    (croupierId) => {
      const introSrc = getCroupierIntroSrc(croupierId);

      return playVoice(introSrc, SFX_VOLUME.croupierIntro);
    },
    [playVoice]
  );

  const playCroupierNumberShout = useCallback(
    (croupierId, result) => {
      const numberShoutSrc = getCroupierNumberShoutSrc(croupierId, result);

      return playVoice(numberShoutSrc, SFX_VOLUME.croupierNumberShout);
    },
    [playVoice]
  );

  const playCroupierNoticeShout = useCallback(
    (croupierId, message) => {
      const noticeShoutSrc = getCroupierNoticeShoutSrc(croupierId, message);

      return playVoice(noticeShoutSrc, SFX_VOLUME.croupierNoticeShout);
    },
    [playVoice]
  );

  const playSettledCroupierNumberSequence = useCallback(
    async ({ croupierId, result } = {}) => {
      await waitForRouletteAudioDelay(ROULETTE_NUMBER_SHOUT_DELAY_MS);
      await playCroupierNumberShout(croupierId, result);
      await waitForRouletteAudioDelay(ROULETTE_RESULT_NOTICE_AFTER_SHOUT_MS);
    },
    [playCroupierNumberShout]
  );

  const stopRouletteSfx = useCallback(() => {
    if (audioRef.current) {
      Object.entries(audioRef.current).forEach(([key, audio]) => {
        fadeOutAudio(audio, fadeTimersRef, key, 80);
      });
    }

    stopVoiceAudio(80);
  }, [stopVoiceAudio]);

  return {
    emitRouletteSfx,
    playCroupierIntro,
    playCroupierNumberShout,
    playCroupierNoticeShout,
    playSettledCroupierNumberSequence,
    stopRouletteSfx,
    unlockRouletteSfx: unlockAudio,
  };
}

export default useRouletteSfx;
